import React from 'react';
import * as R from 'ramda';
import { Debounce } from 'lodash-decorators';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Link } from 'redux-little-router';
import { Table, Dropdown, Radio, Select, Tooltip, Form, List, Card, Tabs, Menu, Input, Layout, Row, Col, Icon, Button, Tree, Avatar } from 'antd';
import '../Toolbox.less';
import { CATEGORY_COLOR, QUERY_COLOR } from '../../Tabs/entityColors';
import Tool from '../../components/Tool';
import EntityIcon from './EntityIcon';
import EntityNodeTitle from './EntityNodeTitle';

const StyledMenu = styled(Menu)`
  .ant-menu-sub {
    background: none !important;
  }
`;

const StyledDirectoryTree = styled(Tree.DirectoryTree)`
  .ant-tree-node-content-wrapper {
    &:before {
      display: none;
    }
  }
  
  /*
  .directory > .ant-tree-node-content-wrapper {
    &:before {
      display: initial;
    }
  }
  */
  
  .ant-tree-iconEle {
    margin-right: 4px !important;
  }
    
`;

const ExploreTable = styled(Table)`
  & .ant-table {
    border: none !important;
    background: #344554;
    border-top: 1px rgba(0, 0, 0, 0.3) solid !important;
  }
  
  & .ant-table-row-expand-icon {
    background: none;
    color: #fff;
  }
  
  & .ant-table-thead th {
    background: #4D5E6D !important;
    border-bottom: 1px rgba(0, 0, 0, 0.2) solid !important;
  }
  
  & .ant-table-row td {
    border-color: rgba(0, 0, 0, 0.3) !important;
    color: rgba(255, 255, 255, 0.8) !important;
    padding: 8px;
  }
  
  & .ant-table-row td a {
    color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      color: #027abf;
    }
  }
  
  & .ant-table-row > td:first-child {
    padding-left: 16px;
  }
  
   & .ant-table-row > td:last-child {
    padding-right: 16px;
  }
  
  & .ant-table-row:hover td {
    background: rgba(255, 255, 255, 0.05) !important;
  }
  
  .exploreTableHover {
    visibility: hidden;
  }
  
  & .ant-table-row:hover .exploreTableHover {
    visibility: visible;
  }
  
  & .ant-table-placeholder {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const columns = [{
  title: 'Name',
  dataIndex: 'name',
  key: 'name',
  width: '100%',
  render: (value, record) => (
    record.type === 'folder'
      ? (
        <span>
          <span style={{ textAlign: 'left', marginRight: '8px', verticalAlign: 'top' }}>
            <Icon type="folder" theme="filled" />
          </span>
          <div
            style={{
              display: 'inline-block',
              textOverflow: 'ellipsis',
              maxWidth: '200px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              verticalAlign: 'top',
            }}
          >
            {value}
          </div>
        </span>
      )
      : (
        <span>
          <span style={{ textAlign: 'left', marginRight: '8px' }}>
            <EntityIcon type={record.type} />
          </span>
          <div
            style={{
              display: 'inline-block',
              textOverflow: 'ellipsis',
              maxWidth: '200px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              verticalAlign: 'top',
            }}
          >
            {value}
          </div>
        </span>
      )
  ),
}, {
  title: '',
  dataIndex: 'id',
  key: 'link',
  render: (value, record) => (
    record.type !== 'folder' && (
      <span className="exploreTableHover">
        <Link
          replaceState
          href={`/tab?type=${record.type}&recordId=${encodeURIComponent(record.id)}`}
        >
          <Icon type="select" />
        </Link>
      </span>
    )
  ),
}];

const mapStateToProps = (state) => {
  return {
    categories: R.pathOr([], ['categoryCollection', 'collection'], state),
    queries: Object.values(R.pathOr({}, ['query', 'byId'], state)),
    recentCategories:
      R.difference(
        Object.keys(state.category.byId),
        state.category.keyWindows
          .map(keyWindow => R.pathOr([], ['category', keyWindow, 'ids'], state))
          .reduce((acc, ids) => acc.concat(ids), []),
      ).map(id => state.category.byId[id]),
    recentQueries:
      R.difference(
        Object
          .keys(state.query.byId)
          .map(key => parseInt(key, 10)),
        state.query.keyWindows
          .map(keyWindow => R.pathOr([], ['query', keyWindow, 'ids'], state))
          .reduce((acc, ids) => acc.concat(ids), []),
      ).map(id => state.query.byId[id]),
    currentConnection: R.pathOr([], ['connection', '/window', 'ids'], state)
      .map(id => state.connection.byId[id])
      .pop(),
    isLoading: !!R.path(['categoryCollection', '__loading'], state),
  };
};

class Index extends React.Component {
  state = {
    view: 'all',
    filterTerm: null,
  };

  handleViewChange = ({ key }) => {
    this.setState({ view: key });
  };

  toggleFilter = () => {
    const { filterTerm } = this.state;
    this.setState({ filterTerm: filterTerm === null ? '' : null });
  };

  @Debounce(200)
  handleApplyFilterTerm = (event) => {
    this.setState({
      filterTerm: event.target.value,
    });
  };

  getEverythingEntries() {
    const { categories, queries } = this.props;

    return [{
      key: 'categories',
      name: 'Categories',
      type: 'folder',
      children: categories.map(category => ({
        key: `categories#${category.id}`,
        type: 'category',
        id: category.id,
        name: category.name,
      })),
    }, {
      key: 'queries',
      name: 'Queries',
      type: 'folder',
      children: queries.map(query => ({
        key: `queries#${query.id}`,
        type: 'query',
        id: query.id,
        name: query.name || `Query ${query.id}`,
      })),
    }];
  }

  getRecentlyClosedEntries() {
    const { recentCategories, recentQueries } = this.props;

    return [].concat(
      recentCategories.map(category => ({
        key: `categories#${category.id}`,
        type: 'category',
        id: category.id,
        name: category.name,
      })),
      recentQueries.map(query => ({
        key: `queries#${query.id}`,
        type: 'query',
        id: query.id,
        name: query.name || `Query ${query.id}`,
      })),
    );
  }

  getFilteredEntries() {
    const { categories, queries, recentCategories, recentQueries } = this.props;
    const { filterTerm } = this.state;

    return (
      []
        .concat(
          R
            .uniq([].concat(categories, recentCategories))
            .map(category => ({
              key: `categories#${category.id}`,
              type: 'category',
              id: category.id,
              name: category.name,
            })),
          R
            .uniq([].concat(queries, recentQueries))
            .map(query => ({
              key: `quyeries#${query.id}`,
              type: 'query',
              id: query.id,
              name: query.name || `Query ${query.id}`,
            })),
        )
        .filter(record => (
          filterTerm === '' || (
            record.name.includes(filterTerm)
            || record.type.includes(filterTerm)
          )
        ))
    );
  }

  render() {
    const { categories, queries, recentCategories, recentQueries } = this.props;
    const { view, filterTerm } = this.state;

    let tableData = [];
    if (filterTerm !== null) {
      tableData = this.getFilteredEntries();
    } else if (view === 'all') {
      tableData = this.getEverythingEntries();
    } else if (view === 'recent') {
      tableData = this.getRecentlyClosedEntries();
    }

    const menu = (
      <Menu
        selectedKeys={[view]}
        onClick={this.handleViewChange}
      >
        <Menu.Item key="all">
          Everything
        </Menu.Item>
        <Menu.Item key="recent">
          Recently Closed
        </Menu.Item>
      </Menu>
    );

    return (
      <Tool
        title="Explore"
        subTitle={(
          <Dropdown overlay={menu} trigger={['click']}>
            <span style={{ cursor: 'pointer' }}>
              {view === 'all' ? 'Everything' : 'Recently Closed'}
              <Icon style={{ paddingLeft: '4px' }} type="caret-down" />
            </span>
          </Dropdown>
        )}
        actions={filterTerm === null && [
          <Tool.Header.Action
            icon="search"
            onClick={this.toggleFilter}
          />,
        ]}
        filterTerm={filterTerm}
        onFilter={filterTerm !== null && this.handleApplyFilterTerm}
        onCancel={filterTerm !== null && this.toggleFilter}
      >
        <div style={{ padding: '8px' }}>
          <ExploreTable
            pagination={false}
            columns={columns}
            dataSource={tableData}
            showHeader={false}
            defaultExpandAllRows
          />
        </div>
      </Tool>
    );
  }
}

export default connect(mapStateToProps)(Index);
